"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

type Props = {
	initialTheme?: "light" | "dark";
};

// A small, dependency-free theme toggle that persists to localStorage and cookie (for SSR).
export default function ThemeToggle({ initialTheme }: Props) {
	const [theme, setTheme] = useState<"light" | "dark">(
		initialTheme ?? "dark"
	);
	const [mounted, setMounted] = useState(false);

	// Read initial theme on mount
	useEffect(() => {
		const stored =
			typeof window !== "undefined"
				? (localStorage.getItem("theme") as "light" | "dark" | null)
				: null;
		const cookieMatch =
			typeof document !== "undefined"
				? document.cookie.match(/(?:^|; )theme=([^;]+)/)
				: null;
		const fromCookie = cookieMatch
			? (decodeURIComponent(cookieMatch[1]) as "light" | "dark")
			: null;
		const prefersLight =
			typeof window !== "undefined" &&
			window.matchMedia &&
			window.matchMedia("(prefers-color-scheme: light)").matches;
		const initial =
			fromCookie ??
			stored ??
			initialTheme ??
			(prefersLight ? "light" : "dark");
		setTheme(initial);
		setMounted(true);
	}, [initialTheme]);

	// Apply theme to <html> attribute and persist in both localStorage and cookie
	useEffect(() => {
		if (!mounted) return;
		const root = document.documentElement;
		root.setAttribute("data-theme", theme);
		try {
			localStorage.setItem("theme", theme);
			// Cookie: 365 days, path=/, sameSite=lax
			const expires = new Date(
				Date.now() + 365 * 24 * 60 * 60 * 1000
			).toUTCString();
			document.cookie = `theme=${encodeURIComponent(
				theme
			)}; Expires=${expires}; Path=/; SameSite=Lax`;
		} catch {}
	}, [theme, mounted]);

	const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

	// Avoid hydration mismatch; render a minimal icon-only button; label is sr-only
	const label = theme === "dark" ? "Light" : "Dark";
	const icon =
		theme === "dark" ? (
			// Sun icon
			<svg
				width="16"
				height="16"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
				aria-hidden
			>
				<circle cx="12" cy="12" r="4" />
				<path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
			</svg>
		) : (
			// Moon icon
			<svg
				width="16"
				height="16"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
				aria-hidden
			>
				<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
			</svg>
		);

	return (
		<Button
			onClick={toggle}
			variant="ghost"
			size="sm"
			aria-label="Toggle theme"
			title={`Switch to ${label} theme`}
		>
			{icon}
			<span className="sr-only">Switch to {label} theme</span>
		</Button>
	);
}
